import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Table from "@/components/Table/table";
import { IconoirMoreHoriz } from "@ey-xd/motif-icon";
import {
  customPaginationPageSize,
  customPaginationPageSizeSelector,
  customTableLoadingData,
} from "@/components/Table/CustomPaginationConstants";
import { havingAddPermission, havingEditPermission } from "@/utility/common";
import Button from "@/components/Form/Button/Button";
import { CREATE_BUTTON_VARIANT } from "@/components/Images/CommonImages";
import CustomPagination from "@/components/Pagination/CustomPagination";
import {
  useGetPendingTransactionsQuery,
  useResolvePendingTransactionsMutation,
} from "@/services/paymentStatus/paymentStatusReducer";
import { useTransactionColumnDefs } from "./transactionTable/TransactionColumnDefs";
import DeleteModal from "@/components/Modal/DeleteModal/DeleteModal";
import NotificationGrid from "@/components/Notification/NotificationGrid";
import { setComponentState1 } from "@/features/retain_state/retainStateSlice";
import {
  MotifButton,
  MotifDropdown,
  MotifDropdownItem,
  MotifIcon,
} from "@ey-xd/motif-react";
import BankActivitiesUploader from "../statusTable/BankActivitiesUploader";

const BankTransactions = () => {
  const { t } = useTranslation();
  const { caseId } = useParams();
  const [errorMessage, setErrorMessage] = useState("");
  const [showResolvedPopup, setShowResolvedPopup] = useState(false);
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<
    string[]
  >([]);
  const [showBankActivityModal, setShowBankActivityModal] = useState(false);
  const [paymentSuccessMessage, setPaymentSuccessMessage] = useState<any>({
    show: false,
    type: "",
    message: "",
  });
  const dispatch = useDispatch();

  const retainState = useSelector(
    (e: any) => e.retainState.bankTransactionListTabState
  );
  const statusTitleMenu: any = [];

  const handleMenuClick = (value: string) => {
    if (value === "uploadBankActivities") {
      setShowBankActivityModal(true);
      return;
    }
  };
  const paginationPageSize: number = customPaginationPageSize;
  const paginationPageSizeSelector: number[] = customPaginationPageSizeSelector;
  let tableLoadingData = customTableLoadingData;
  const getPaginationObj: any = useMemo(() => {
    let value: any = {
      CaseId: caseId,
      PageNumber: retainState?.PageNumber || 1,
      PageSize: retainState?.PageSize || paginationPageSize,
      SearchText: retainState?.SearchText || "",
    };
    if (retainState?.SortOrder && retainState?.SortColumn) {
      value.SortOrder = retainState?.SortOrder;
      value.SortColumn = retainState?.SortColumn;
    }
    return value;
  }, [caseId, paginationPageSize, retainState]);
  const paginationObj = getPaginationObj;
  const {
    data: transactionData,
    isLoading: isTransactionDataLoading,
    isFetching,
    error,
    refetch,
    status,
  } = useGetPendingTransactionsQuery(paginationObj);
  const [resolvePendingTransaction, { isLoading: resolvedLoading }] =
    useResolvePendingTransactionsMutation();
  const rowData = transactionData?.results ? transactionData.results : [];
  const permissions = useSelector((state: any) => state.permissions);
  const { Payment, ImportJob } = permissions;
  const headerData = {
    title: "pages.paymentStatus.bankTransactions.tableTitle",
    description: "pages.paymentStatus.bankTransactions.tableDescription",
  };

  const transactionColumnDefs = useTransactionColumnDefs(Payment);
  if (havingEditPermission(ImportJob)) {
    statusTitleMenu.push({
      label: "pages.paymentStatus.moreOptionsLables.uploadBankActivities",
      value: "uploadBankActivities",
    });
  }
  if (isTransactionDataLoading) {
    return <div>{t("global.globalNotifications.loadingMessage")}</div>;
  }
  function handlePaginationData(data: any, columnTypesData: any) {
    dispatch(
      setComponentState1({
        entityName: "bankTransactionListTabState",
        entityState: {
          SortOrder: data.SortOrder,
          SortColumn: data.SortColumn,
          sortedColumnTypes: columnTypesData,
        },
      })
    );
  }

  const onRowSelected = (selectedRows: any) => {
    const selectedNode = selectedRows.node;
    const selectedData = selectedNode.data;
    const isSelected = selectedNode.isSelected();
    const { bankTransactionId } = selectedData;
    setSelectedTransactionIds((prevSelectedIds) =>
      isSelected
        ? [...prevSelectedIds, bankTransactionId]
        : prevSelectedIds.filter((id) => id !== bankTransactionId)
    );
  };

  const handleResolveTransaction = async () => {
    setErrorMessage("");
    await resolvePendingTransaction(selectedTransactionIds).then(
      (response: any) => {
        if (response.data) {
          refetch();
          setSelectedTransactionIds([]);
          setShowResolvedPopup(false);
        } else {
          if (response.error.data?.title) {
            setErrorMessage(response.error.data.title);
          } else {
            setErrorMessage(response.error.data?.message);
          }
          setShowResolvedPopup(false);
        }
      }
    );
  };

  return (
    <div>
      {errorMessage && <NotificationGrid message={errorMessage} type="error" />}
      {paymentSuccessMessage.show && (
        <div className="mar-t-20">
          <NotificationGrid
            show={paymentSuccessMessage.show}
            message={paymentSuccessMessage.message}
            type={paymentSuccessMessage.type}
            onClose={() =>
              setPaymentSuccessMessage({ show: false, message: "", type: "" })
            }
            scrollToTop={false}
          />
          <br></br>
        </div>
      )}
      <Table
        columnDefs={transactionColumnDefs}
        columnTypes={{ ...retainState.sortedColumnTypes }}
        handlePaginationData={handlePaginationData}
        paginationObj={paginationObj}
        cellRenderers={isFetching ? [] : rowData}
        TitleComplement={() => (
          <>
            {havingAddPermission(Payment) ? (
              <Button
                label={t("pages.paymentStatus.bankTransactions.markAsResolved")}
                variant={CREATE_BUTTON_VARIANT}
                onClickHandler={() => {
                  setShowResolvedPopup(true);
                }}
                disabled={!selectedTransactionIds.length}
              />
            ) : (
              <></>
            )}
            {statusTitleMenu.length > 0 && (
              <MotifDropdown
                ariaLabelledby="dropdown-trigger-1"
                id="dropdown-1"
                trigger={
                  <MotifButton
                    aria-label="dropdpwn"
                    id="dropdown-trigger-1"
                    onClick={function Ga() {}}
                    type="button"
                    variant="text"
                  >
                    <MotifIcon iconFunction={IconoirMoreHoriz} />
                  </MotifButton>
                }
              >
                <ul role="menu">
                  {statusTitleMenu.map((menuName: any, index: any) => {
                    return (
                      <li role="menuitem" key={index}>
                        <MotifDropdownItem
                          indexItem={1}
                          onClick={() => handleMenuClick(menuName.value)}
                        >
                          {t(menuName.label)}
                        </MotifDropdownItem>
                      </li>
                    );
                  })}
                </ul>
              </MotifDropdown>
            )}
          </>
        )}
        title={headerData.title}
        description={headerData.description}
        pagination={false}
        overlayNoRowsTemplate={
          isFetching
            ? t(tableLoadingData)
            : error
            ? t("global.globalNotifications.wentWrong")
            : t("global.globalNotifications.noRecords")
        }
        rowSelection="multiple"
        onRowSelected={onRowSelected}
      />
      <CustomPagination
        apiData={transactionData}
        apiStatus={status}
        currentpage={paginationObj.PageNumber}
        pageSize={paginationObj.PageSize}
        disableNext={!transactionData?.hasMoreRecords}
        onChangePageSize={(page: number) => {
          dispatch(
            setComponentState1({
              entityName: "bankTransactionListTabState",
              entityState: {
                PageSize: page,
                PageNumber: 1,
              },
            })
          );
        }}
        onChangeNextPage={() => {
          dispatch(
            setComponentState1({
              entityName: "bankTransactionListTabState",
              entityState: {
                PageNumber: paginationObj.PageNumber + 1,
              },
            })
          );
        }}
        onChangePreviousPage={() => {
          dispatch(
            setComponentState1({
              entityName: "bankTransactionListTabState",
              entityState: {
                PageNumber: paginationObj.PageNumber - 1,
              },
            })
          );
        }}
        pageSizeSelector={paginationPageSizeSelector}
      />
      {showResolvedPopup && (
        <DeleteModal
          message={t(
            "pages.paymentStatus.bankTransactions.transactionResolvedMessage"
          )}
          isVisible={true}
          setIsVisible={setShowResolvedPopup}
          cancelBtnHandler={() => setShowResolvedPopup(false)}
          yesBtnHandler={handleResolveTransaction}
          deleteLoading={resolvedLoading}
        />
      )}
      {showBankActivityModal && (
        <BankActivitiesUploader
          showModal={true}
          setShowModal={setShowBankActivityModal}
          title={t("pages.paymentStatus.bankActivitiesImport.title")}
          uploadLabel={t("pages.claims.claimsTable.bulkImport.uploadLabel")}
          uploadDescription={t(
            "pages.paymentStatus.bankActivitiesImport.uploadDescription"
          )}
          onSucessRefreshApi={() => {
            refetch();
            setSelectedTransactionIds([]);
          }}
          maxFiles={1}
          onClose={() => {
            setShowBankActivityModal(false);
            setPaymentSuccessMessage({
              show: false,
              message: "",
              type: "",
            });
          }}
        />
      )}
    </div>
  );
};
export default BankTransactions;
